import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ActionMenu, type ActionMenuItem } from './ActionMenu'
import { getNlsString } from '../../nls/nlsRuntime'
import { computeCollapseOrder, planToolbarOverflow } from './overflowToolbarHelpers'
import './overflowToolbar.css'

export type OverflowToolbarItem = {
  key: string
  node: ReactNode
  grow?: boolean
} & (
  | { pinned: true; menuItems?: never; priority?: never }
  | { pinned?: false; menuItems: ActionMenuItem[]; priority?: number }
)

export type OverflowToolbarProps = {
  items: OverflowToolbarItem[]
  ariaLabel: string
  className?: string
}

export function OverflowToolbar({
  items,
  ariaLabel,
  className,
}: OverflowToolbarProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [hiddenIndices, setHiddenIndices] = useState<number[]>([])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const update = () => {
      const host = hostRef.current
      const measure = measureRef.current
      if (!host || !measure) return

      const available = host.clientWidth || host.getBoundingClientRect().width
      const count = items.length
      const widths: number[] = []
      for (let i = 0; i < count; i++) {
        const el = measure.children[i]
        const w = el instanceof HTMLElement ? el.offsetWidth || el.getBoundingClientRect().width : 0
        widths.push(w)
      }
      const moreEl = measure.children[count]
      const moreWidth =
        moreEl instanceof HTMLElement ? moreEl.offsetWidth || moreEl.getBoundingClientRect().width : 0

      const order = computeCollapseOrder(items)
      const hidden = planToolbarOverflow(widths, available, moreWidth, 8, order)
      setHiddenIndices(hidden)
    }

    update()

    const observer = new ResizeObserver(update)
    if (hostRef.current) observer.observe(hostRef.current)
    if (measureRef.current) observer.observe(measureRef.current)
    return () => observer.disconnect()
  }, [items])

  const hiddenSet = new Set(hiddenIndices)
  const visibleItems = items.filter((_, idx) => !hiddenSet.has(idx))

  const kebabItems: ActionMenuItem[] = []
  if (hiddenIndices.length > 0) {
    hiddenIndices.forEach((hiddenIdx, groupIndex) => {
      const item = items[hiddenIdx]
      if (!item || item.pinned || !item.menuItems) return
      item.menuItems.forEach((menuItem, itemIndex) => {
        if (groupIndex > 0 && itemIndex === 0) {
          kebabItems.push({
            ...menuItem,
            dividerBefore: true,
          })
        } else {
          kebabItems.push(menuItem)
        }
      })
    })
  }

  return (
    <div
      ref={hostRef}
      role="toolbar"
      aria-label={ariaLabel}
      className={['dsOverflowToolbar', className].filter(Boolean).join(' ')}
    >
      {visibleItems.map((item) => (
        <div
          key={item.key}
          className={item.grow ? 'dsOverflowToolbar__item--grow' : undefined}
        >
          {item.node}
        </div>
      ))}

      {hiddenIndices.length > 0 && (
        <ActionMenu
          trigger="kebab"
          ariaLabel={getNlsString('NLSCommonOverflowToolbarMore')}
          items={kebabItems}
        />
      )}

      <div ref={measureRef} className="dsOverflowToolbarMeasure" aria-hidden="true">
        {items.map((item) => (
          <div
            key={item.key}
            className={item.grow ? 'dsOverflowToolbar__item--grow' : undefined}
            style={item.grow ? { flex: '0 0 120px', width: 120, minWidth: 120, maxWidth: 120 } : undefined}
          >
            {item.node}
          </div>
        ))}
        <ActionMenu trigger="kebab" ariaLabel="" items={[]} />
      </div>
    </div>
  )
}
